import { useState, useEffect, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const ArticleCarousel = ({ recentArticles = [] }) => {
  const [articles, setArticles] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const autoPlayRef = useRef(null);

  // SOLUCIÓN: Usar directamente las props, sin fetch adicional
  useEffect(() => {
    if (recentArticles.length > 0) {
      setArticles(recentArticles);
    }
  }, [recentArticles]);

  // Resto del código del carousel permanece igual...
  // Auto-play cada 5 segundos
  useEffect(() => {
    if (articles.length > 0 && !isPaused) {
      autoPlayRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % articles.length);
      }, 5000);
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [articles.length, isPaused]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % articles.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + articles.length) % articles.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  if (articles.length === 0) {
    return (
      <div className="w-full h-96 bg-gray-200 animate-pulse rounded-lg flex items-center justify-center">
        <div className="text-gray-500">Cargando artículos...</div>
      </div>
    );
  }

  const currentArticle = articles[currentSlide];

  return (
    <div
      className="relative w-full h-96 overflow-hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Contenedor de slides con animación */}
      <div className="relative w-full h-full">
        {articles.map((article, index) => (
          <div
            key={article.id}
            className={`absolute inset-0 transition-transform duration-500 ${
              index === currentSlide
                ? 'transform translate-x-0'
                : index < currentSlide
                ? 'transform -translate-x-full'
                : 'transform translate-x-full'
            }`}
          >
            {/* Fondo con imagen y overlay oscuro */}
            <div className="absolute inset-0">
              <img
                src={article.featured_image_url || '/default-image.jpg'}
                alt={article.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black bg-opacity-50"></div>
            </div>

            {/* Contenido central */}
            {index === currentSlide && (
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <div className="text-center text-white max-w-7xl px-4">
                  <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-[#ebffd0ff]">
                    {article.title}
                  </h2>

                  <div className="w-full inline-block xl:flex items-center justify-center xl:space-x-4 mb-6 text-sm md:text-base">
                    <div className="hidden sm:flex items-center space-x-2 justify-center">
                      <span className="text-gray-300">Categorías:</span>
                      <div className="flex space-x-1">
                        {article.categories
                          .flatMap((cat) =>
                            // Aplanar recursivamente y filtrar solo las que no tienen hijos
                            (function flattenLeaves(category) {
                              if (
                                category.children &&
                                category.children.length > 0
                              ) {
                                return category.children.flatMap(flattenLeaves);
                              }
                              return [category];
                            })(cat)
                          )
                          .map((category, idx, array) => (
                            <span key={category.id} className="font-medium">
                              {category.name}
                              {idx < array.length - 1 && ','}
                            </span>
                          ))}
                      </div>
                    </div>

                    <span className="hidden xl:flex text-gray-300">•</span>

                    <div className="flex items-center space-x-2 justify-center">
                      <span className="text-gray-300">Por:</span>
                      <span className="font-medium">
                        {article.author?.display_name || 'Anónimo'}
                      </span>
                    </div>

                    <span className="hidden xl:flex text-gray-300">•</span>

                    <div className="flex items-center space-x-2 justify-center">
                      <span>
                        {new Date(article.published_at).toLocaleDateString(
                          'es-ES',
                          {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          }
                        )}
                      </span>
                    </div>
                  </div>

                  <a
                    href={'/' + article.slug}
                    className="bg-[#52BA2F] hover:bg-[#3DA11B] text-white px-6 py-2 rounded-lg font-semibold transition duration-300 transform hover:scale-105"
                  >
                    LEER MÁS
                  </a>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Flechas de navegación */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-40 hover:bg-opacity-60 text-white p-2 rounded-full transition duration-300 z-20"
      >
        <ChevronLeftIcon className="h-6 w-6" />
      </button>

      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-40 hover:bg-opacity-60 text-white p-2 rounded-full transition duration-300 z-20"
      >
        <ChevronRightIcon className="h-6 w-6" />
      </button>

      {/* Indicadores de puntos */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
        {articles.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition duration-300 ${
              index === currentSlide
                ? 'bg-white'
                : 'bg-white bg-opacity-50 hover:bg-opacity-70'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default ArticleCarousel;
